import React from 'react';
import { useSelector } from 'react-redux';
import env from '@beam-australia/react-env';
import { PAYFAIL_OVERVIEW, PAYFAIL_ONGOING } from 'constants/routeConstants';
import exclamationMarkOrange from 'assets/images/icons/exclamation-mark-orange.svg';
import phoneIcon from 'assets/images/icons/phone.svg';
import {
  PAYFAIL_CALL_US_NUMBER, PAYFAIL_CALL_US_HREF, PAYFAIL_CALL_US_BODY,
  PAYFAIL_CALL_US_EARLY_CANCELLED_TITLE, PAYFAIL_CALL_US_EARLY_CANCELLED_BODY,
} from 'constants/payfailConstants';
import trackEvent from 'services/AnalyticsService';
import { Store } from 'types/store';
import styles from './PayfailAlert.module.scss';

const PayfailAlert = () => {
  const session = useSelector((state: Store) => state.session);
  const { writeOffAndDiscountPercentage, isEarlyCancelledMandate, isMonthlyOnCard, isEligibleForOnlineResolution } = useSelector((state: Store) => state.session.payfailAlertData) || {};

  // Determine step to redirect user to
  const linkRef = session?.showPayfailStepTwoAlert ? PAYFAIL_ONGOING : PAYFAIL_OVERVIEW;
  const hasDiscount = Boolean(writeOffAndDiscountPercentage);

  let title = 'Your payment has failed';
  let description = (
    <p>
      Your last payment for breakdown cover didn’t go through. Let’s fix it –
      {hasDiscount && (
        <> <strong>pay now and save {writeOffAndDiscountPercentage}%.</strong></>
      )}<br />
      <u>Manage payments</u>
    </p>
  );

  // Members who can't resolve online are asked to call, whatever else applies
  const isCallUs = isEligibleForOnlineResolution === false;

  if (isCallUs) {
    if (isEarlyCancelledMandate) title = PAYFAIL_CALL_US_EARLY_CANCELLED_TITLE;
    description = (
      <p>
        {isEarlyCancelledMandate ? PAYFAIL_CALL_US_EARLY_CANCELLED_BODY : PAYFAIL_CALL_US_BODY}
      </p>
    );
  } else if (isMonthlyOnCard) {
    title = 'Restart your monthly card payments';
    description = (
      <p>
        Never miss a payment again
        {hasDiscount && (
          <> &mdash; and <strong>save {writeOffAndDiscountPercentage}%</strong></>
        )}<br />
        <u>Set up now</u>
      </p>
    );
  } else if (isEarlyCancelledMandate) {
    title = 'Restart your direct debit';
    description = (
      <p>
        We noticed you cancelled your direct debit. Restart it today to keep your breakdown cover
        {hasDiscount && (
          <> and <strong>save {writeOffAndDiscountPercentage}%</strong></>
        )}<br />
        <u>Restart now</u>
      </p>
    );
  }

  if (isCallUs) {
    return (
      <div className={`${styles.payfailCard} ${styles.payfailCardStatic}`}>
        <div className={styles.payfailCardImage}>
          <img
            src={exclamationMarkOrange}
            alt="Payfail warning"
          />
        </div>
        <div className={styles.payfailCardContent}>
          <div className={styles.payfailCardHeader}>
            <h3>{title}</h3>
          </div>
          {description}
          <a
            className={styles.callLink}
            href={PAYFAIL_CALL_US_HREF}
            onClick={() => trackEvent('MyRAC_PayfailCallUs_Alert_CallClicked')}
          >
            <img src={phoneIcon} alt="" />
            {PAYFAIL_CALL_US_NUMBER}
          </a>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={styles.payfailCard}
      onClick={() => {
        window.location.href = `${env('HOME_URL')}${linkRef}`
      }}
    >
      <div className={styles.payfailCardImage}>
        <img
          src={exclamationMarkOrange}
          alt="Payfail warning"
        />
      </div>
      <div className={styles.payfailCardContent}>
        <div className={styles.payfailCardHeader}>
          <h3>{title}</h3>
        </div>
        {description}
      </div>
    </button>
  );
}

export default PayfailAlert;
